// Two-factor authentication settings.
//
// Enrollment is two-step by design (see src/api/mfa.ts): `startEnrollment`
// issues a secret but leaves MFA OFF, and only `confirmEnrollment` — which
// needs a working code from the user's authenticator — switches it on. Never
// collapse this into a single "Enable" button: if the QR scan silently failed,
// a one-step enable locks the user out of their own account.
//
// Backup codes are shown exactly once. The server stores only SHA-256 digests
// (services/auth/database.py `hash_backup_code`), so there is no "show them
// again" endpoint to add later — the only recovery is to regenerate.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mfaAPI, type MFAEnrollment } from '../../api/mfa';
import { useToast } from '../../hooks/useToast';
import { logger } from '../../utils/logger';
import Modal from '../Modal/Modal';
import { Button, Input } from '../ui';
import styles from './MFAPanel.module.css';

type Stage = 'idle' | 'scan' | 'codes';

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export default function MFAPanel() {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [stage, setStage] = useState<Stage>('idle');
  const [enrollment, setEnrollment] = useState<MFAEnrollment | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [confirmCode, setConfirmCode] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState('');

  const statusQuery = useQuery({
    queryKey: ['mfa', 'status'],
    queryFn: () => mfaAPI.status(),
  });

  const refreshStatus = () => queryClient.invalidateQueries({ queryKey: ['mfa', 'status'] });

  const startMutation = useMutation({
    mutationFn: () => mfaAPI.startEnrollment(),
    onSuccess: (data) => {
      setEnrollment(data);
      setConfirmCode('');
      setShowSecret(false);
      setStage('scan');
    },
    onError: (error) => {
      logger.error('MFA enrollment failed to start:', error);
      addToast(errorMessage(error, 'Could not start two-factor setup'), 'error');
    },
  });

  const confirmMutation = useMutation({
    mutationFn: (code: string) => mfaAPI.confirmEnrollment(code),
    onSuccess: (data) => {
      setBackupCodes(data.backup_codes);
      setEnrollment(null);
      setStage('codes');
      void refreshStatus();
      addToast('Two-factor authentication is on', 'success');
    },
    onError: (error) => {
      addToast(errorMessage(error, 'That code was not accepted'), 'error');
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => mfaAPI.disable(disablePassword, disableCode),
    onSuccess: () => {
      setDisableOpen(false);
      setDisablePassword('');
      setDisableCode('');
      void refreshStatus();
      addToast('Two-factor authentication is off', 'success');
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Could not turn off two-factor authentication'), 'error');
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: () => mfaAPI.regenerateBackupCodes(regenerateCode),
    onSuccess: (data) => {
      setBackupCodes(data.backup_codes);
      setRegenerateOpen(false);
      setRegenerateCode('');
      setStage('codes');
      void refreshStatus();
    },
    onError: (error) => {
      addToast(errorMessage(error, 'Could not create new backup codes'), 'error');
    },
  });

  const status = statusQuery.data;
  const enabled = status?.enabled ?? false;

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      addToast('Backup codes copied', 'success');
    } catch {
      // Clipboard is unavailable over plain HTTP and in some embedded views.
      // The codes are on screen either way, so this is not worth an error toast.
      addToast('Copy is unavailable here — please write the codes down', 'info');
    }
  };

  return (
    <section className={styles.panel}>
      <header className={styles.header}>
        <div>
          <h2 className={styles.title}>Two-factor authentication</h2>
          <p className={styles.subtitle}>
            Protect your account with a code from an authenticator app, in addition to your
            password.
          </p>
        </div>
        <span className={enabled ? styles.badgeOn : styles.badgeOff}>
          {statusQuery.isLoading ? 'Checking…' : enabled ? 'On' : 'Off'}
        </span>
      </header>

      {statusQuery.isError && (
        <p className={styles.error}>Could not load your two-factor status.</p>
      )}

      {/* --- Enabled ------------------------------------------------------ */}
      {enabled && stage !== 'codes' && (
        <div className={styles.body}>
          <p className={styles.line}>
            You have <strong>{status?.backup_codes_remaining ?? 0}</strong> unused backup{' '}
            {status?.backup_codes_remaining === 1 ? 'code' : 'codes'}.
          </p>
          {(status?.backup_codes_remaining ?? 0) <= 2 && (
            <p className={styles.warning}>
              You are running low on backup codes. Create a new set so you do not lose access if
              you lose your phone.
            </p>
          )}
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setRegenerateOpen(true)}>
              Create new backup codes
            </Button>
            <Button variant="danger" onClick={() => setDisableOpen(true)}>
              Turn off
            </Button>
          </div>
        </div>
      )}

      {/* --- Not enrolled ------------------------------------------------- */}
      {!enabled && stage === 'idle' && (
        <div className={styles.body}>
          {status?.enrollment_pending && (
            <p className={styles.line}>
              You started setting this up but never finished, so it is still off. Starting again
              will issue a new code.
            </p>
          )}
          <Button
            onClick={() => startMutation.mutate()}
            disabled={startMutation.isPending}
            loading={startMutation.isPending}
          >
            Set up two-factor authentication
          </Button>
        </div>
      )}

      {/* --- Step 1: scan and confirm ------------------------------------- */}
      {stage === 'scan' && enrollment && (
        <div className={styles.body}>
          <ol className={styles.steps}>
            <li>Open your authenticator app and scan this code.</li>
            <li>Enter the 6-digit code it shows to finish.</li>
          </ol>

          <img
            className={styles.qr}
            src={`data:image/png;base64,${enrollment.qr_code}`}
            alt="QR code for setting up two-factor authentication"
          />

          <button
            type="button"
            className={styles.link}
            onClick={() => setShowSecret((v) => !v)}
          >
            {showSecret ? 'Hide setup key' : 'Can’t scan? Enter a key instead'}
          </button>
          {showSecret && <code className={styles.secret}>{enrollment.secret}</code>}

          <form
            className={styles.confirmRow}
            onSubmit={(e) => {
              e.preventDefault();
              confirmMutation.mutate(confirmCode.trim());
            }}
          >
            <Input
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Code from your authenticator app"
            />
            <Button
              type="submit"
              disabled={confirmMutation.isPending || confirmCode.trim().length < 6}
              loading={confirmMutation.isPending}
            >
              Confirm
            </Button>
            <button
              type="button"
              className={styles.link}
              onClick={() => {
                setStage('idle');
                setEnrollment(null);
              }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* --- Step 2: backup codes, shown once ----------------------------- */}
      {stage === 'codes' && (
        <div className={styles.body}>
          <p className={styles.line}>
            <strong>Save these backup codes now.</strong> Each one works once, and this is the
            only time they are shown — we store them scrambled, so we cannot show them again.
          </p>
          <ul className={styles.codes}>
            {backupCodes.map((code) => (
              <li key={code}>
                <code>{code}</code>
              </li>
            ))}
          </ul>
          <div className={styles.actions}>
            <Button variant="secondary" onClick={copyCodes}>
              Copy codes
            </Button>
            <Button
              onClick={() => {
                setBackupCodes([]);
                setStage('idle');
              }}
            >
              I have saved them
            </Button>
          </div>
        </div>
      )}

      {/* --- Turn off ----------------------------------------------------- */}
      <Modal
        isOpen={disableOpen}
        onClose={() => setDisableOpen(false)}
        title="Turn off two-factor authentication"
      >
        <form
          className={styles.modalForm}
          onSubmit={(e) => {
            e.preventDefault();
            disableMutation.mutate();
          }}
        >
          <p className={styles.line}>
            This removes a layer of protection from your account. We need your password and a
            current code to confirm it is really you.
          </p>
          <Input
            type="password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            placeholder="Your password"
            autoComplete="current-password"
            aria-label="Your password"
          />
          <Input
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            placeholder="Authenticator or backup code"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label="Authenticator or backup code"
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setDisableOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="danger"
              disabled={
                disableMutation.isPending || !disablePassword || disableCode.trim().length < 6
              }
              loading={disableMutation.isPending}
            >
              Turn off
            </Button>
          </div>
        </form>
      </Modal>

      {/* --- Regenerate backup codes -------------------------------------- */}
      <Modal
        isOpen={regenerateOpen}
        onClose={() => setRegenerateOpen(false)}
        title="Create new backup codes"
      >
        <form
          className={styles.modalForm}
          onSubmit={(e) => {
            e.preventDefault();
            regenerateMutation.mutate();
          }}
        >
          <p className={styles.line}>
            This replaces your existing backup codes. Any code you have written down will stop
            working.
          </p>
          <Input
            value={regenerateCode}
            onChange={(e) => setRegenerateCode(e.target.value)}
            placeholder="Authenticator or backup code"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label="Authenticator or backup code"
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setRegenerateOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={regenerateMutation.isPending || regenerateCode.trim().length < 6}
              loading={regenerateMutation.isPending}
            >
              Create new codes
            </Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}
